import Vue from 'vue'
import {HTTP} from '@/utils/httpCommonUtil'
import {BallPulseLoader} from 'vue-loaders';
import VueSimpleSpinner from 'vue-simple-spinner'
import {getRestParamErrorMessage, getRestResponseErrorMessage} from '@/utils/serverErrorMessageUtil';
import {adminRoles, UserRoleEnum} from '@/enums/userRolesEnum'
import GenericModal from '@/componentsReusable/genericModal.vue';
import UploadControl from '@/componentsReusable/UploadControl.vue';
import {convertDateStringToUTCDate} from '@/utils/dateUtil';
import MaskedInput from 'vue-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import ShiftEventHub from '@/components/shift/ShiftEventHub';
import flatPickr from 'vue-flatpickr-component';

export default Vue.component('accounting-information-card', {
  name: 'AccountingInformationCard',
  data () {
    let emptyArray: Array<any> = [];
    return {
      cardExpanded: false,
      loadingInitialData: false,
      savingNewTransaction: false,
      invoiceWeeks: emptyArray,
      isAccountingFrozen: false,
      isAuthorizedToDelete: false,
      showDeleteTransactionConfirmationModal: false,
      showDeleteDocumentConfirmationModal: false,
      pendingDeleteTransaction: null,
      pendingDeleteDocument: null,
      systemTransactionTypes: [],
      rateMask: createNumberMask({
        prefix: '',
        allowDecimal: true,
        decimalLimit: 2,
        integerLimit: 6,
        thousandsSeparatorSymbol: '',
        allowNegative: true
      }),
      mileageRateMask: createNumberMask({
        prefix: '',
        allowDecimal: true,
        decimalLimit: 3,
        integerLimit: 4,
        thousandsSeparatorSymbol: ''
      }),
      quantityMask: createNumberMask({
        prefix: '',
        allowDecimal: true,
        decimalLimit: 2,
        integerLimit: 4,
        thousandsSeparatorSymbol: ''
      }),
      distance: 0,
      tranIdsForinvoiceShowsNotes: new Set([12,13,14,15,16,17,18]) // These Transaction Type IDs will have any Notes shown on the Invoice
    }
  },
  components: {
    BallPulseLoader,
    VueSimpleSpinner,
    UploadControl,
    GenericModal,
    MaskedInput,
    flatPickr
  },
  computed: {
    isProviderUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.PROVIDER)
    },
    isFacilityUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.FACILITY_CONTACT)
    }
  },
  beforeMount: function () {
    this.isAuthorizedToDelete = this.$store.getters['user/hasRole'](adminRoles);
  },
  created: function () {
    this.expandCard();
    ShiftEventHub.$on('SHIFT_PROVIDER_CHANGE', this.initializeCard);
    ShiftEventHub.$on('SHIFT_DATETIME_CHANGE', this.initializeCard)
  },
  beforeDestroy: function () {
    ShiftEventHub.$off('SHIFT_PROVIDER_CHANGE', this.initializeCard);
    ShiftEventHub.$off('SHIFT_DATETIME_CHANGE', this.initializeCard)
  },
  methods: {
    expandCard () {
      this.cardExpanded = !this.cardExpanded;
      if (this.cardExpanded) {
        this.initializeCard();
      }
    },
    initializeCard () {
      this.getTransactions();
      this.getSystemTransactionTypes()
    },
    getSystemTransactionTypes () {
      if (this.isProviderUser || this.isFacilityUser) {
        return;
      }
      let self = this;
      let showLoadingSpinnerTimeout = setTimeout(function () {
        self.loadingInitialData = true;
      }, 500);
      HTTP.get('/api/shifts/' + this.$route.params.shiftId + '/transaction-types')
      .then(function (response) {
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving System Transaction Types.");
        }
        let freshTransactionTypes = response.data.data;
        freshTransactionTypes.forEach( function (transactionType: any) {
          transactionType.billRateCalculated = transactionType.billRateCalculated == null ? '' : '' + transactionType.billRateCalculated;
          transactionType.payRateCalculated = transactionType.payRateCalculated == null ? '' : '' + transactionType.payRateCalculated;
        });
        self.systemTransactionTypes = freshTransactionTypes;
      })
      .catch(function () {
        console.log('response Error!');
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred retrieving System Transaction Types.'
        });
      })
    },
    preserveOpenItems (existingItems: any, newItems: any, uniqueIdTier1: string, uniqueIdTeir2: string, childItemArray1: string) {
      existingItems.forEach( function (existingItem: any) {
        newItems.forEach ( function (newItem: any) {
          if (existingItem[uniqueIdTier1] === newItem[uniqueIdTier1]) {
            newItem.isFocused = existingItem.isFocused;

            existingItem[childItemArray1] = existingItem[childItemArray1].filter(function (obj: any) { return !obj.isNew; });

            // If the length of existingItem[childItemArray1] < newItem[childItemArray1], it's probably an "Insert"
            if (existingItem[childItemArray1].length && (existingItem[childItemArray1].length < newItem[childItemArray1].length)) {
              // Find the new item and set "isEditing" to true
              let tempNewItem = newItem[childItemArray1].filter(function (obj1: any) {
                return !existingItem[childItemArray1].some(function (obj2: any) {
                  return obj1[uniqueIdTeir2] === obj2[uniqueIdTeir2];
                });
              });
              tempNewItem.forEach( function (obj: any) { obj.isEditing = true; } );
            } else {
              // If the length of existingItem[childItemArray1] === newItem[childItemArray1], it's probably an "Update"
              existingItem[childItemArray1].forEach(function (existingItem: any) {
                if (existingItem.isEditing) {
                  newItem[childItemArray1].forEach(function (newItem: any) {
                    if (newItem[uniqueIdTeir2] === existingItem[uniqueIdTeir2]) {
                      newItem.isEditing = existingItem.isEditing;
                    }
                  })
                }
              })
            }

          }
        })
      });

      return newItems;
    },
    getTransactions () {
      let self = this;
      let showLoadingSpinnerTimeout = setTimeout(function () {
        self.loadingInitialData = true;
      }, 0);
      HTTP.get('/api/shifts/' + this.$route.params.shiftId + '/transactions')
      .then(function (response) {
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Transactions.");
        }
        let freshInvoiceWeeksOfTransactions = response.data.data.results;
        self.distance = response.data.data.distance;
        let isFocused = true;
        freshInvoiceWeeksOfTransactions.forEach( function (invoiceWeekOfTransactions: any) {          
          invoiceWeekOfTransactions.isFocused = isFocused;
          isFocused = false;
          invoiceWeekOfTransactions.shiftTransactions.forEach( function (transaction: any) {
            transaction.quantity = '' + transaction.quantity;
            if(transaction.transactionTypeId !== 11) {
              transaction.billRateCalculated = (+transaction.billRateCalculated).toFixed(2);
              transaction.billRateCalculatedWithoutManualOverride = (+transaction.billRateCalculatedWithoutManualOverride).toFixed(2);
              transaction.payRateCalculated = (+transaction.payRateCalculated).toFixed(2);
              transaction.payRateCalculatedWithoutManualOverride = (+transaction.payRateCalculatedWithoutManualOverride).toFixed(2);
            } else {
              transaction.billRateCalculated = (+transaction.billRateCalculated).toFixed(3);
              transaction.billRateCalculatedWithoutManualOverride = (+transaction.billRateCalculatedWithoutManualOverride).toFixed(3);
              transaction.payRateCalculated = (+transaction.payRateCalculated).toFixed(3);
              transaction.payRateCalculatedWithoutManualOverride = (+transaction.payRateCalculatedWithoutManualOverride).toFixed(3);
            }
            transaction.isEditing = false;
            transaction.isNew = false;
            transaction.updatingData = false;
            transaction.receiptDate = Boolean(transaction.receiptDate) ? convertDateStringToUTCDate(transaction.receiptDate) : transaction.receiptDate;
            transaction.isFrozen = Boolean(invoiceWeekOfTransactions.invoiceData) || transaction.accountingFrozen;
          });
        });

        self.invoiceWeeks = self.preserveOpenItems(self.invoiceWeeks, freshInvoiceWeeksOfTransactions, 'invoiceStartDate', 'shiftTransactionId', 'shiftTransactions');
      })
      .catch(function (err) {
        console.log('response Error! ' + err);
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred retrieving the shift\'s transactions.'
        });
      })
    },
    transactionTypeChange (currentTransaction: any) {
      this.systemTransactionTypes.forEach( (transactionType: any) => {
        if (transactionType.transactionTypeId === currentTransaction.transactionTypeId) {
          if (currentTransaction.transactionTypeId === 11) {
            currentTransaction.quantity = this.distance;
          }
          if (!currentTransaction.manualOverride) {
            currentTransaction.billRateCalculated = transactionType.billRateCalculated;
            currentTransaction.payRateCalculated = transactionType.payRateCalculated;
          }
          currentTransaction.transactionTypeQuantityLabel = parseInt(currentTransaction.quantity, 10) === 1 ? transactionType.transactionTypeQuantityLabel : transactionType.transactionTypeQuantityPluralLabel;
          if (transactionType.couldHaveReceipt) {
            currentTransaction.couldHaveReceipt = true;
            currentTransaction.expectingReceipt = true;
            currentTransaction.quantity = 1;
            currentTransaction.manualOverride = true;
          }
        }
      });
    },
    manualOverrideChange (currentTransaction: any) {
      if (!currentTransaction.manualOverride) {
        currentTransaction.billRateCalculated = currentTransaction.billRateCalculatedWithoutManualOverride;
        currentTransaction.payRateCalculated = currentTransaction.payRateCalculatedWithoutManualOverride;
      }
    },
    saveTransaction (newTransaction: any) {
      let self = this;
      self.savingNewTransaction = true;
      newTransaction.receiptDate = Boolean(newTransaction.receiptDate) ? newTransaction.receiptDate : null;
      HTTP.post('/api/shifts/' + this.$route.params.shiftId + '/transactions', newTransaction)
      .then(function (response) {
        self.savingNewTransaction = false;
        newTransaction.isEditing = false;
        self.getTransactions();
      })
      .catch(function (error) {
        self.savingNewTransaction = false;

        newTransaction.transactionTypeIdErrorMessage = getRestParamErrorMessage(error, 'transactionTypeId');
        newTransaction.quantityErrorMessage = getRestParamErrorMessage(error, 'quantity');
        newTransaction.billRateCalculatedErrorMessage = getRestParamErrorMessage(error, 'billRateCalculated');
        newTransaction.payRateCalculatedErrorMessage = getRestParamErrorMessage(error, 'payRateCalculated');

        if (
          !newTransaction.transactionTypeIdErrorMessage.length &&
          !newTransaction.quantityErrorMessage.length &&
          !newTransaction.billRateCalculatedErrorMessage.length &&
          !newTransaction.payRateCalculatedErrorMessage.length
        ) {
          let generalError = getRestResponseErrorMessage(error);
          self.$notify({
            group: 'main',
            type: 'error',
            title: 'Error Saving',
            text: generalError ? generalError : 'An error occurred saving transaction.',
            // duration: 5000
          });
        }

      })
    },
    updateTransaction (updatedTransaction: any) {
      let self = this;
      updatedTransaction.updatingData = true;
      updatedTransaction.receiptDate = Boolean(updatedTransaction.receiptDate) ? updatedTransaction.receiptDate : null;
      HTTP.put('/api/shifts/' + this.$route.params.shiftId + '/transactions/' + updatedTransaction.shiftTransactionId, updatedTransaction)
      .then(function (response) {
        updatedTransaction.updatingData = false;
        // updatedTransaction.isEditing = false;
        self.getTransactions();
        // self.$notify({
        //   group: 'main',
        //   type: 'success',
        //   title: 'Successfully Saved',
        //   text: 'This Transaction was updated successfully.',
        //   // duration: 5000
        // });
      })
      .catch(function (error) {
        updatedTransaction.updatingData = false;

        updatedTransaction.transactionTypeIdErrorMessage = getRestParamErrorMessage(error, 'transactionTypeId');
        updatedTransaction.quantityErrorMessage = getRestParamErrorMessage(error, 'quantity');
        updatedTransaction.billRateCalculatedErrorMessage = getRestParamErrorMessage(error, 'billRateCalculated');
        updatedTransaction.payRateCalculatedErrorMessage = getRestParamErrorMessage(error, 'payRateCalculated');

        if (
          !updatedTransaction.transactionTypeIdErrorMessage.length &&
          !updatedTransaction.quantityErrorMessage.length &&
          !updatedTransaction.billRateCalculatedErrorMessage.length &&
          !updatedTransaction.payRateCalculatedErrorMessage.length
        ) {
          let generalError = getRestResponseErrorMessage(error);
          self.$notify({
            group: 'main',
            type: 'error',
            title: 'Error Updating',
            text: generalError ? generalError : 'An error occurred updating transaction.',
            // duration: 5000
          });
        }

      })
    },
    deleteTransactionConfirmed (deleteTransaction: any) {
      this.showDeleteTransactionConfirmationModal = false;
      this.deleteTransaction(this.pendingDeleteTransaction, true);
    },
    deleteTransaction (deleteTransaction: any, bypassDeleteConfirmation: boolean = false) {
      // To prevent a double submit
      if (deleteTransaction.updatingData) { console.log("returning."); return ; }

      if (!bypassDeleteConfirmation) {
        // Show Delete Confirmation Modal
        this.showDeleteTransactionConfirmationModal = true;
        this.pendingDeleteTransaction = deleteTransaction;
        return;
      } else {
        this.pendingDeleteTransaction = null;
      }
      let self = this;
      deleteTransaction.updatingData = true;
      HTTP.delete('/api/shifts/' + this.$route.params.shiftId + '/transactions/' + deleteTransaction.shiftTransactionId)
      .then(function (response) {
        deleteTransaction.updatingData = false;
        deleteTransaction.isEditing = false;
        self.getTransactions();
        // self.$notify({
        //   group: 'main',
        //   type: 'success',
        //   title: 'Successfully Deleted',
        //   text: 'Transaction successfully deleted.',
        //   // duration: 5000
        // });
      })
      .catch(function (error) {
        deleteTransaction.updatingData = false;

        deleteTransaction.transactionTypeIdErrorMessage = getRestParamErrorMessage(error, 'transactionTypeId');
        deleteTransaction.quantityErrorMessage = getRestParamErrorMessage(error, 'quantity');
        deleteTransaction.billRateCalculatedErrorMessage = getRestParamErrorMessage(error, 'billRateCalculated');
        deleteTransaction.payRateCalculatedErrorMessage = getRestParamErrorMessage(error, 'payRateCalculated');

        if (
          !deleteTransaction.transactionTypeIdErrorMessage.length &&
          !deleteTransaction.quantityErrorMessage.length &&
          !deleteTransaction.billRateCalculatedErrorMessage.length &&
          !deleteTransaction.payRateCalculatedErrorMessage.length
        ) {
          let generalError = getRestResponseErrorMessage(error);
          self.$notify({
            group: 'main',
            type: 'error',
            title: 'Error Deleting',
            text: generalError ? generalError : 'An error occurred deleting transaction.',
            // duration: 5000
          });
        }

      })
    },
    canDeleteTransaction(invoiceWeek: any) {
      const transactionIds = invoiceWeek.shiftTransactions.map((transaction : any) => transaction.shiftTransactionId);
      return new Set(transactionIds).size > 1;
    },
    toggleEditTransaction (transaction: any, transactions: any) {
      if (this.isProviderUser || this.isFacilityUser) {
        return;
      }
      let selectedCredId = transaction.transactionId;
      // Collapse any editing transactions
      // Delete any existing "new" transactions
      let indexesToDelete: any = [];
      transactions.forEach(function (transaction: any, index: any) {
        if (transaction.isNew) {
          indexesToDelete.push(index);
        }
        if (selectedCredId !== transaction.transactionId) {
          transaction.isEditing = false;
        }
      });
      indexesToDelete.forEach(function (indx: number) {
        transactions.splice(indx,1);
      });
      transaction.isEditing = !transaction.isEditing;
    },
    addTransaction (invoiceWeek: any) {
      // Collapse any editing transactions
      // Delete any existing "new" transactions
      let indexesToDelete: any = [];
      let transactions = invoiceWeek.shiftTransactions;
      transactions.forEach(function (transaction: any, index: any) {
        if (transaction.isNew) {
          indexesToDelete.push(index);
        }
        transaction.isEditing = false;
      });
      indexesToDelete.forEach(function (indx: number) {
        transactions.splice(indx,1);
      });

      let newTransaction = {
        "shiftTransactionId": this.$route.params.shiftId,
        "invoiceStartDate": invoiceWeek.invoiceStartDate,
        "transactionTypeId": null,
        "transactionTypeName": null,
        "transactionTypeQuantityLabel": null,
        "quantity": null,
        "manualOverride": false,
        "billRateCalculated": null,
        "payRateCalculated": null,
        "manualBillRate": null,
        "manualPayRate": null,
        "note": null,
        "isEditing": true,
        "isNew": true,
        "updatingData": false,
        expectingReceipt: false,
        receiptDate: null,
        couldHaveReceipt: false
      };
      this.calculateMissingReceipt(newTransaction);

      transactions.push(newTransaction);
    },
    cancel (transaction: any, transactions: any) {
      transaction.isEditing = false;
      if (transaction.isNew) {
        // Allow the editing UI to collapse before removing it from the array.
        setTimeout(function () {
          transactions.pop();
        }, 300);
      } else {
        this.getTransactions();
      }
    },
    associateUploadedFile (s3FilePath: string, transactionId: any, fileName: string, transaction: any) {
      let self = this;
      const entityType = 16; // This is found in the DB. In this case this type means "TRANSACTION"
      HTTP.post('/api/attachment/', {
        "entityId": transactionId,
        "entityType": entityType,
        "attachmentName": fileName,
        "s3Key": s3FilePath,
        "exposeToFacility": true
      })
      .then(function (response) {
        if (!response.data || !response.data.success) {
          throw new Error("Error associating uploaded file.");
        }
        self.getTransactions();
        transaction.attachments.push({
          id: response.data.data.attachmentId,
          name: fileName,
          s3Key: s3FilePath,
          exposeToFacility: true
        });
        self.calculateMissingReceipt(transaction);
        self.getTransactions();
      })
      .catch(function (err: any) {
        console.log('response Error!');
        console.log(err);
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded file attachment.'
        });
      })
    },
    openAttachment (s3Key: string) {
      let self = this;
      HTTP.get('/api/s3/signed-url/get?s3key=' + s3Key)
      .then(function (response) {
        if (!response.data || !response.data.data) {
          throw new Error("Error getting attachment.");
        }
        console.log('signed URL: ' + response.data.data);
        window.open(response.data.data, '_blank');
      })
      .catch(function (err: any) {
        console.log('response Error!');
        console.log(err);
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded file attachment.'
        });
      })
    },
    deleteDocumentConfirmed (id: any) {
      this.showDeleteDocumentConfirmationModal = false;
      this.deleteTransactionDocument(this.pendingDeleteDocument, true );
    },
    async updateExposeToFacilities (attachmentId: number, exposedToFacilities: boolean) {
      let self = this
      let showLoadingSpinnerTimeout = setTimeout(function () {
        self.loadingInitialData = true;
      }, 0);

      await HTTP.put(`/api/attachments/${attachmentId}`, { exposedToFacilities: (exposedToFacilities) ? 1 : 0})
      .then( () => {
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        self.getTransactions();
      })
      .catch( () => {
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred updating Exposed to Facility.'
        });
      });
    },
    deleteTransactionDocument (attachmentId: any, bypassDeleteConfirmation: boolean = false ) {
      if (!bypassDeleteConfirmation) {
        // Show Delete Confirmation Modal
        this.showDeleteDocumentConfirmationModal = true;
        this.pendingDeleteDocument = attachmentId;
        return;
      } else {
        this.pendingDeleteDocument = null;
      }
      let self = this
      let showLoadingSpinnerTimeout = setTimeout(function () {
        self.loadingInitialData = true;
      }, 0);
      HTTP.delete('/api/attachments/' + attachmentId )
      .then(function (response) {
        self.getTransactions();
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
      })
      .catch(function (err) {
        console.log(err);
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred deleting Transaction Document.'
        });
      });
    },
    focusInvoiceWeek (invoiceWeek: any, invoiceWeeks: any) {
      invoiceWeeks.forEach( function (invoiceWeekInstance: any) {
        invoiceWeekInstance.isFocused = false;
      });
      invoiceWeek.isFocused = true;
    },
    browseToTransaction (transaction: any) {
      window.open("/facilities/" + transaction.facilityId + '/specialties/' + transaction.specialtyId, '_blank');
    },
    browseToInvoice (invoiceId: number) {
      window.open("/invoices/" + invoiceId, '_blank');
    },
    browseToPotentialInvoice(transaction: any) {
      if (transaction.mergedInvoiceId) {
        window.open("/invoices/merged/" + transaction.mergedInvoiceId, '_blank');
      } else {
        const date = new Date(transaction.invoiceStartDate);
        const invoiceDate = date.toISOString().slice(0,10);
        window.open("/facilities/" + transaction.facilityId + '/specialties/' + transaction.specialtyId + '/invoices/date/' + invoiceDate, '_blank');
      }
    },
    calculateMissingReceipt(transaction: any) {
      transaction.missingReceipt = transaction.expectingReceipt && (!transaction.receiptDate || transaction.attachments.length === 0);
    },
    billRateChanged(transaction: any) {
      this.calculateMissingReceipt(transaction);
    },
    expectingReceiptChanged(transaction: any) {
      this.calculateMissingReceipt(transaction);
    },
    receiptDateChanged(transaction: any) {
      this.calculateMissingReceipt(transaction);
    }
  }
})



// WEBPACK FOOTER //
// ./src/components/shift/cards/accountingInformation/AccountingInformationCard.ts